import {
  AccountantListItem,
  applyDecisionEvent,
  AttachableDocumentType,
  AttachmentFollowUp,
  AttachmentType,
  AuditorListItem,
  BRSigner,
  canApplyDecision,
  CompanyContact,
  countIncorporationConfirmationDocuments,
  DbotEvent,
  DbotEventDispatcher,
  DecisionAttachment,
  defaultIncorporationActivationSubscription,
  DocumentItem,
  downloadSignedDocumentEvent,
  downloadSignedSubscriptionForms,
  DseStatusResponse,
  Evaluation,
  findStateForDecision,
  FollowUp,
  FollowUpStatus,
  FollowUpType,
  getDecisionName,
  GetProjectResponse,
  getSigningStatusEvent,
  getSubscriptionStatusEvent,
  IncorporationActivationInfo,
  IncorporationActivationSubscription,
  IncorporationDocumentPart,
  isDecisionApplied,
  isIncorporationDocument,
  makeCountryRadioFieldModel,
  OpenReviewDialogAction,
  OpenSendForReviewDialogAction,
  OpenSubscriptionFormBatchPreviewDialogAction,
  ProjectContext,
  ProjectDocumentStatus,
  refreshProjectStatusEvent,
  reopenDocumentEvent,
  selectedDestinationFolderModel,
  prekontrollIncorporationEvent,
  SendToBRDecision,
  sendIncorporationToBREvent,
  sendToBREvent,
  shareholdersWithShares,
  skipSendToBREvent,
  updateFollowUpStatusEvent,
  uploadAttachmentsEvent,
  BundledDocument,
} from "@/dbot/common/commonDomain";
import { makeAutoObservable } from "mobx";
import {
  canBeSentForVoting,
  CompanyBRContext,
  ConfirmSendIncorporationToBRDialogModel,
  ConfirmSendToBRDialogModel,
  DecisionAction,
  DecisionListItem,
  DialogState,
  getBoardRecipients,
  getBoardRecipientsEligibleForPowerOfAttorney,
  getBoardSignersInfo,
  getShareholderRecipients,
  hasInvalidRecipients,
  isSendToBRReady,
  noDialog,
  PackageSigningDialogModel,
  ReopenLockedDocumentDialogModel,
  regeneratedIncorporationDocuments,
  ReviewDocumentSigningDialogModel,
  ReviewSendNoticeOfBoardMeetingDialogModel,
  ReviewSendNoticeOfGeneralMeetingDialogModel,
  ReviewSendSubscriptionFormDialogModel,
  BundleSelectionDialogModel,
  SendForReviewDialogModel,
  SendToBRMatter,
  signingEmailBody,
  ViewDocumentSigningStatusDialogModel,
  ViewDocumentViewingStatusDialogModel,
  ViewSubscriptionFormBatchPreviewModel,
  ViewSubscriptionStatusDialogModel,
} from "./summaryDomain";
import { dNumberValidator, formatOrgNum, norwegianPersonalIdValidator } from "@/utils/other";
import {
  emailValidator,
  isFailure,
  isOk,
  pending,
  Result,
  SharepointSettingsForDocument,
} from "@/common/common";
import { DocumentSigningStatus } from "@/document/documentApi";
import { compact, debounce, groupBy, remove } from "lodash";
import {
  ExcessAmountMethod,
  findIncreaseOfShareCaptial,
  ShareAllocationRoundsModel,
  SubscribersStatusModel,
  toIncreaseOfShareCapitalSubscribersStatus,
} from "@/dbot/increaseOfShareCapital/increaseOfShareCapitalDomain";
import { getFullName, sortedBoardAndCEO } from "@/utils/Roles";
import { DocumentType } from "@/common/documents";
import { DecisionType } from "@/common/decisions";
import {
  BooleanFieldModel,
  createEmailField,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { isCountryCodeNorway, isCountryCodeSweden, norway } from "@/utils/countries";
import { confirmAction, httpGet, httpPost, showAlert, thunk } from "@/events/eventsDomain";
import {
  SharepointContentInDrive,
  SharepointContext,
  SharepointDrive,
} from "@/sharepoint/sharepointDomain";
import { Language } from "@/i18n/languages";
import { when } from "@/utils/functions";
import { TMessage } from "@/i18n/tt";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { SuggestedCompany } from "@/types/CompanyInfo";

export class SummaryController {
  readonly dispatch: DbotEventDispatcher;
  readonly context: ProjectContext;
  isRefreshingSigningStatuses = false;
  dialogState: DialogState = noDialog();
  followUps: GetProjectResponse["followUps"] = [];
  isApplying: { decisionId: string; orgNo: string }[] = [];
  isSendingToBR: { decisionId: string; orgNo: string }[] = [];
  isSkippingSendToBR: { decisionId: string; orgNo: string }[] = [];
  // Keyed by decisionId. Lifetime is the page session — we don't persist it server-side because
  // the response is a snapshot of BR's outbound mailbox at call time, not a durable status.
  isCheckingIncorporationStatus: Set<string> = new Set();
  incorporationStatusByDecisionId: Map<string, Result<DseStatusResponse, TMessage>> = new Map();
  incorporationActivationInfo?: Result<IncorporationActivationInfo, TMessage>;
  activationSubscriptionByDecisionId: Map<string, IncorporationActivationSubscription> = new Map();
  isCreatingMergerCounterpart = false;

  constructor(
    dispatch: DbotEventDispatcher,
    context: ProjectContext,
    followUps: GetProjectResponse["followUps"],
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.dispatch = dispatch;
    this.context = context;
    this.followUps = followUps;
  }

  toDocumentListItem(d: DocumentItem): DocumentListItem {
    return {
      document: d,
      signingStatus: this.getSigningStatusForDocument(d),
      numberOfSigners: this.getDocumentStatus(d)?.numberOfSigners ?? 0,
      numberOfSignatures: this.getDocumentStatus(d)?.numberOfSignatures ?? 0,
      incorporationDocumentPart: this.getDocumentStatus(d)?.incorporationDocumentPart,
      isRefreshingSigningStatus: this.isRefreshingSigningStatuses,
      sendingTime: this.getDocumentSendingTime(d),
      showDocumentListItem:
        d.type !== DocumentType.articlesofassociation && d.type !== DocumentType.subscription_form,
    };
  }

  get decisionsForApply() {
    return this.context.companies.flatMap(({ orgNo }) =>
      compact(
        this.context.currentDecisions.map((d, i, decisions) => {
          const decisionFollowUps = this.followUps.find(
            (f) => f.orgNo === orgNo && f.decisionId === d.decisionId,
          );
          if (!decisionFollowUps) {
            return undefined;
          }
          const previousDecisionFollowUps =
            i > 0
              ? this.followUps.find(
                  (f) => f.orgNo === orgNo && f.decisionId === decisions[i - 1].decisionId,
                )
              : undefined;
          const isApplying = this.isApplying.some(
            (x) => x.orgNo === orgNo && x.decisionId === d.decisionId,
          );
          const isSendingToBR = this.isSendingToBR.some(
            (x) => x.orgNo === orgNo && x.decisionId === d.decisionId,
          );
          const isPreviousDecisionApplied =
            previousDecisionFollowUps === undefined
              ? true
              : !canApplyDecision(previousDecisionFollowUps.followUps) ||
                isDecisionApplied(previousDecisionFollowUps.followUps);

          return new DecisionListItem({
            orgNo,
            decision: d,
            followUps: decisionFollowUps.followUps,
            eventLogs: decisionFollowUps.eventLogs,
            isApplying,
            isSendingToBR,
            isEnabled: decisionFollowUps.isEnabled,
            isPreviousDecisionApplied: isPreviousDecisionApplied,
            uploadedDecisionAttachments: decisionFollowUps.uploadedDecisionAttachments,
            fileUploadConfig: this.context.fileUploadConfig,
            documents: this.context.currentDocuments.map((document) =>
              this.toDocumentListItem(document),
            ),
            roles: this.context.contextData[orgNo].initialState.roles,
            isCheckingIncorporationStatus: this.isCheckingIncorporationStatus.has(d.decisionId),
            incorporationStatusResult: this.incorporationStatusByDecisionId.get(d.decisionId),
            activationInfo: this.incorporationActivationInfo,
            activationSubscription: this.activationSubscriptionByDecisionId.get(d.decisionId),
            isCreatingMergerCounterpart: this.isCreatingMergerCounterpart,
          });
        }),
      ),
    );
  }

  updateSubscriptionFormFollowUp(documentId: string) {
    for (const decisionFollowUp of this.followUps) {
      if (
        decisionFollowUp.followUps.find(
          (d) => d.data.type === FollowUpType.subscriptionForm && d.data.documentId === documentId,
        )
      ) {
        this.dispatch(
          updateFollowUpStatusEvent(decisionFollowUp.orgNo, decisionFollowUp.decisionId, {
            type: FollowUpType.subscriptionForm,
            documentId: documentId,
          }),
        );
      }
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/reopen-document-result":
        if (
          this.dialogState.type === "reopenLockedDocument" ||
          this.dialogState.type === "viewDocumentSigningStatus"
        ) {
          this.dialogState.model.setReopenResult(event.reopenResult);
          if (isOk(event.reopenResult)) {
            this.dispatch(refreshProjectStatusEvent());
            this.updateSubscriptionFormFollowUp(event.documentId);
            this.isRefreshingSigningStatuses = true;
            this.closeDialog();
          }
        }
        break;
      case "dbot/refresh-project-status-result":
        this.isRefreshingSigningStatuses = false;
        break;
      case "dbot/update-follow-up-status-result":
      case "dbot/fetch-decision-status-result":
        this.followUps = event.data.followUps;
        break;
      case "dbot/apply-decision-result":
        remove(
          this.isApplying,
          (x) => x.orgNo === event.orgNo && x.decisionId === event.decisionId,
        );
        break;
      case "dbot/send-to-br-result":
        remove(
          this.isSendingToBR,
          (x) => x.orgNo === event.orgNo && x.decisionId === event.decisionId,
        );
        if (
          this.dialogState.type === "confirmSendToBR" ||
          this.dialogState.type === "confirmSendIncorporationToBR"
        ) {
          this.dialogState.model.handleEvent(event);
        }
        break;
      case "dbot/skip-send-to-br-result":
        remove(
          this.isSkippingSendToBR,
          (x) => x.orgNo === event.orgNo && x.decisionId === event.decisionId,
        );
        break;
      case "dbot/create-merger-counterpart":
        this.isCreatingMergerCounterpart = true;
        break;
      case "dbot/create-merger-counterpart-result":
        this.isCreatingMergerCounterpart = false;
        if (isFailure(event.result)) {
          this.dispatch(showAlert(event.result.error, "error"));
        }
        break;
      case "dbot/check-incorporation-status":
      case "dbot/simulate-br-response":
        this.isCheckingIncorporationStatus.add(event.decisionId);
        break;
      case "dbot/check-incorporation-status-result":
        this.isCheckingIncorporationStatus.delete(event.decisionId);
        this.incorporationStatusByDecisionId.set(event.decisionId, event.result);
        break;
      case "dbot/fetch-incorporation-activation-info":
        this.incorporationActivationInfo = pending();
        break;
      case "dbot/fetch-incorporation-activation-info-result":
        this.incorporationActivationInfo = event.result;
        break;
      case "dbot/select-incorporation-activation-subscription":
        this.activationSubscriptionByDecisionId.set(event.decisionId, event.subscription);
        break;
      case "dbot/prekontroll-incorporation-result":
        if (this.dialogState.type === "confirmSendIncorporationToBR") {
          this.dialogState.model.handleEvent(event);
        }
        break;
      case "dbot/get-signing-status-result":
      case "dbot/resend-document-for-signing-result":
        if (this.dialogState.type === "viewDocumentSigningStatus") {
          this.dialogState.model.setSigningStatusResult(event.result);
        }
        break;
      case "dbot/get-subscription-status-result":
        if (this.dialogState.type === "viewSubscriptionStatus") {
          this.dialogState.model.setSubscriptionStatusResult(event.result);
        }
        break;
      case "dbot/download-document":
        this.dispatch(downloadSignedDocumentEvent(event.document.documentId, event.document.type));
        break;
      case "dbot/download-subscription-forms":
        this.dispatch(downloadSignedSubscriptionForms(event.document.documentId));
        break;
      case "dbot/validate-project-action":
        if (
          this.context.currentDecisions.length > 0 ||
          this.context.currentDocuments.every((x) => x.type === DocumentType.custom_document)
        ) {
          this.dispatch({
            type: "dbot/validate-project",
            contextDocumentId: event.contextDocumentId,
            onSuccess: event.successAction,
          });
        } else {
          this.dispatch({
            type: "dbot/project-validation-failed",
            validationError: { key: "dbot.status-no-decisions.error" },
          });
        }
        break;
      case "dbot/openSubscriptionFormBatchPreviewDialog":
        this.openSubscriptionFormBatchPreviewDialog(event);
        break;
      case "dbot/open-review-dialog":
        this.openReviewDialog(event);
        break;
      case "dbot/open-bundle-selection-dialog":
        this.dialogState = {
          type: "bundleSelection",
          model: new BundleSelectionDialogModel({
            currentDocument: event.currentDocument,
            documents: event.documents,
            companies: event.companies,
            emailBody: {
              key: "dbot.send-together.email-body",
              params: { senderName: getFullName(this.context.user) },
            },
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
          }),
        };
        break;
      case "dbot/open-package-signing-dialog":
        this.dialogState = {
          type: "packageSigning",
          model: new PackageSigningDialogModel({
            orgNo: this.context.orgNo,
            documents: event.documents,
            companies: event.companies,
            emailBody: event.emailBody,
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
            subscriptionTypeModel: this.context.subscriptionTypeModel,
            aiClient: this.context.aiClient,
            user: this.context.user,
            isBatchProject: this.context.isBatchProject,
            destinationFolders: event.destinationFolders,
            sharepointContext: (documentType) =>
              this.sharepointContext(currentDocumentId(event.documents), documentType),
          }),
        };
        break;
      case "dbot/open-send-for-review-dialog":
        this.openSendForReviewDialog(
          event,
          canBeSentForVoting(this.context.currentDecisions, event.document),
        );
        break;
      case "dbot/open-signing-status-dialog":
        this.openSigningStatusDialog(event);
        break;
      case "dbot/open-viewing-status-dialog":
        this.dialogState = {
          type: "viewDocumentViewingStatus",
          model: new ViewDocumentViewingStatusDialogModel({
            document: event.document,
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
          }),
        };
        this.dialogState.model.init();
        break;
      case "dbot/refresh-signing-status":
        this.dispatch(refreshProjectStatusEvent());
        this.isRefreshingSigningStatuses = true;
        break;
      case "dbot/open-subscription-status":
        this.openSubscriptionStatusDialog(event);
        break;
      case "dbot/open-increase-of-share-capital-subscribers-status":
        when(findIncreaseOfShareCaptial(this.context), (data) => {
          this.dialogState = {
            type: "viewIncreaseOfShareCapitalSubscribersStatus",
            model: {
              type: "status",
              subscriberModel: new SubscribersStatusModel(
                this.dispatch,
                this.closeDialog,
                event.orgNo,
                this.context.initialState,
                this.context.aiClient,
                this.context.documentLanguages,
                this.context.isLocked || event.isLocked,
                findStateForDecision(this.context.inputStates, event.decisionId),
                ExcessAmountMethod.discharged,
                toIncreaseOfShareCapitalSubscribersStatus(data),
              ),
            },
          };
        });
        break;
      case "dbot/open-increase-of-share-capital-subscribers-allocation": {
        const subscriberModel = new ShareAllocationRoundsModel(
          event.orgNo,
          event.decisionId,
          this.dispatch,
          this.closeDialog,
          this.context.initialState.shareTypes,
          findStateForDecision(this.context.inputStates, event.decisionId),
          undefined,
          event.isSentToBR,
        );
        this.dialogState = {
          type: "viewIncreaseOfShareCapitalSubscribersAllocation",
          model: { type: "allocation", subscriberModel },
        };
        subscriberModel.init();
        break;
      }
      case "dbot/unlock-document":
        this.openUnlockDocumentDialog(event.documentId);
        break;
      case "dbot/applyDecisionAction":
        this.dispatch(
          applyDecisionEvent(
            event.orgNo,
            event.decision.decisionId,
            event.decision.type,
            event.decision.type === DecisionType.incorporation
              ? (this.activationSubscriptionByDecisionId.get(event.decision.decisionId) ??
                  defaultIncorporationActivationSubscription)
              : undefined,
          ),
        );
        this.isApplying.push({ orgNo: event.orgNo, decisionId: event.decision.decisionId });
        break;
      case "dbot/confirm-send-to-br":
        this.openConfirmSendToBRDialog(event.orgNo, event.decisionId);
        break;
      case "dbot/confirm-send-incorporation-to-br":
        this.openConfirmSendIncorporationToBRDialog(event.orgNo, event.decisionId);
        break;
      case "dbot/confirm-skip-send-to-br":
        this.openSkipConfirmSendToBRDialog(event.orgNo, event.decisionId);
        break;
      default:
        switch (this.dialogState.type) {
          case "reviewDocumentSigning":
          case "packageSigning":
          case "reviewSendNoticeOfBoardMeeting":
          case "reviewSendNoticeOfGeneralMeeting":
          case "reviewSendSubscriptionForm":
          case "viewDocumentViewingStatus":
          case "sendForReview":
            this.dialogState.model.handleEvent(event);
            break;
          case "viewIncreaseOfShareCapitalSubscribersStatus":
          case "viewIncreaseOfShareCapitalSubscribersAllocation":
            this.dialogState.model.subscriberModel.handleEvent(event);
            break;
        }
    }
  }

  get containsDecisionSentToBR() {
    return this.followUps.some((a) =>
      a.followUps.some(
        (b) => b.data.type === FollowUpType.sendToBR && b.data.brStatus !== "NOT_SENT",
      ),
    );
  }

  private openUnlockDocumentDialog(documentId: string) {
    this.dialogState = {
      type: "reopenLockedDocument",
      model: new ReopenLockedDocumentDialogModel({
        onSubmit: () => this.dispatch(reopenDocumentEvent(documentId)),
        onCancel: this.closeDialog,
        canReopenDocument: !this.containsDecisionSentToBR,
        regeneratedDocuments: regeneratedIncorporationDocuments(
          documentId,
          this.context.currentDocuments,
          this.context.documentStatuses,
        ),
      }),
    };
  }

  getDocumentStatus(document: DocumentItem): ProjectDocumentStatus | undefined {
    return this.context.documentStatuses.find((x) => x.documentId === document.documentId);
  }

  getSigningStatusForDocument(document: DocumentItem): DocumentSigningStatus {
    return this.getDocumentStatus(document)?.signingStatus ?? DocumentSigningStatus.UNKNOWN;
  }

  getDocumentSendingTime(document: DocumentItem): string | undefined {
    return this.getDocumentStatus(document)?.sentAt;
  }

  closeDialog() {
    this.dialogState = noDialog();
  }

  sharepointContext(documentId: string, documentType: DocumentType): SharepointContext {
    return {
      company: this.context.initialState.company,
      user: this.context.user,
      enabled: this.context.isSharepointEnabled,
      documentType,
      loadSharepointDrives: this.loadSharepointDrives,
      loadContentInSharepointDrive: this.loadContentInSharepointDrive,
      saveSharepointDestinationSettings: this.saveSharepointDestinationSettings(documentType),
      settings: findSharepointSettings(this.context, documentId, documentType),
    };
  }

  openReviewDialog(action: OpenReviewDialogAction) {
    const sharepointContext = this.sharepointContext(
      action.document.documentId,
      action.document.type,
    );

    switch (action.document.type) {
      case DocumentType.notice_board_meeting:
        this.dialogState = {
          type: "reviewSendNoticeOfBoardMeeting",
          model: new ReviewSendNoticeOfBoardMeetingDialogModel({
            document: action.document,
            recipients: getBoardRecipients(this.context),
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
            subscriptionTypeModel: this.context.subscriptionTypeModel,
            title: action.label,
            emailBody: action.emailBody ?? {
              key: "dbot.send-for-viewing.board-meeting.email-body",
              params: { name: this.context.initialState.company.name },
            },
            aiClient: this.context.aiClient,
            isResend: action.isResend,
            hasAllSocialSecurityNumbers: hasAllBoardMemberSSNs(this.context),
            hasExtraParticipants: hasExtraParticipants(this.context),
            isSendToNewParticipants: action.isSendToNewParticipants,
            authMethod: action.authMethod,
            meetingPassword: action.meetingPassword,
          }),
        };
        break;
      case DocumentType.notice_general_meeting:
        this.dialogState = {
          type: "reviewSendNoticeOfGeneralMeeting",
          model: new ReviewSendNoticeOfGeneralMeetingDialogModel({
            companyName: this.context.initialState.company.name,
            document: action.document,
            hasInvalidCompanyRoleRecipients: hasInvalidRecipients(
              getBoardRecipientsEligibleForPowerOfAttorney(this.context),
            ),
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
            subscriptionTypeModel: this.context.subscriptionTypeModel,
            shareholderRecipients: getShareholderRecipients(
              this.context.initialState.shareholders,
              this.context.initialState.sharesForShareholders,
            ),
            emailBody: {
              key: "dbot.send-for-viewing.general-meeting.email-body",
              params: { name: this.context.initialState.company.name },
            },
            aiClient: this.context.aiClient,
            documentStatus: this.getDocumentStatus(action.document),
            isMissingShareholderSSN: isMissingShareholderSSN(this.context),
            hasForeignShareholders: hasForeignShareholders(this.context),
          }),
        };
        break;
      case DocumentType.subscription_form:
        this.dialogState = {
          type: "reviewSendSubscriptionForm",
          model: new ReviewSendSubscriptionFormDialogModel({
            document: action.document,
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
            subscriptionTypeModel: this.context.subscriptionTypeModel,
            aiClient: this.context.aiClient,
            user: this.context.user,
            signers: action.signers ?? [],
          }),
        };
        break;
      case DocumentType.board_meeting:
        this.dialogState = {
          type: "reviewDocumentSigning",
          model: new ReviewDocumentSigningDialogModel({
            orgNo: this.context.orgNo,
            document: action.document,
            signers: action.signers ?? [],
            documents: action.documents ?? [],
            companies: action.companies ?? [],
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
            subscriptionTypeModel: this.context.subscriptionTypeModel,
            infoMessage: getBoardSignersInfo(this.context),
            showUnsupportedAttachmentWarning: action.showUnsupportedAttachmentWarning,
            aiClient: this.context.aiClient,
            emailBody: {
              key: "dbot.send-for-signing.email-body",
              params: {
                senderName: getFullName(this.context.user),
                fileName: action.document.title,
              },
            },
            user: this.context.user,
            isBatchProject: this.context.isBatchProject,
            sharepointContext,
            attachments: action.attachments,
          }),
        };
        break;
      case DocumentType.general_meeting:
        this.dialogState = {
          type: "reviewDocumentSigning",
          model: new ReviewDocumentSigningDialogModel({
            orgNo: this.context.orgNo,
            document: action.document,
            signers: action.signers ?? [],
            documents: action.documents ?? [],
            companies: action.companies ?? [],
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
            subscriptionTypeModel: this.context.subscriptionTypeModel,
            showUnsupportedAttachmentWarning: action.showUnsupportedAttachmentWarning,
            aiClient: this.context.aiClient,
            emailBody: {
              key: "dbot.send-for-signing.email-body",
              params: {
                senderName: getFullName(this.context.user),
                fileName: action.document.title,
              },
            },
            user: this.context.user,
            isBatchProject: this.context.isBatchProject,
            sharepointContext,
            attachments: action.attachments,
          }),
        };
        break;
      case DocumentType.custom_document:
        this.dialogState = {
          type: "reviewDocumentSigning",
          model: new ReviewDocumentSigningDialogModel({
            orgNo: this.context.orgNo,
            document: action.document,
            signers: action.signers ?? [],
            documents: action.documents ?? [],
            companies: action.companies ?? [],
            dispatch: this.dispatch,
            closeDialog: this.closeDialog,
            subscriptionTypeModel: this.context.subscriptionTypeModel,
            showUnsupportedAttachmentWarning: action.showUnsupportedAttachmentWarning,
            aiClient: this.context.aiClient,
            emailBody: signingEmailBody(
              action.document,
              this.getDocumentStatus(action.document)?.incorporationDocumentPart,
              getFullName(this.context.user),
              this.context.currentDecisions,
            ),
            user: this.context.user,
            isBatchProject: this.context.isBatchProject,
            sharepointContext,
            destinationFolder: action.preselectedDestinationFolder,
            lockedSigningMethod: action.lockedSigningMethod,
            destinationFolderModel: selectedDestinationFolderModel(
              action.destinationFolders,
              action.preselectedDestinationFolder,
            ),
            openReceiptDialog: isIncorporationDocument(action.document)
              ? this.openIncorporationSendingReceiptDialog
              : undefined,
            attachments: action.attachments,
          }),
        };
        break;
    }
  }

  openIncorporationSendingReceiptDialog() {
    this.dialogState = {
      type: "incorporationSendingReceipt",
      confirmationDocumentCount: countIncorporationConfirmationDocuments(this.followUps),
    };
  }

  loadSharepointDrives(): Promise<SharepointDrive[]> {
    return new Promise((resolve, reject) => {
      this.dispatch([
        httpGet({
          url: `/sharepoint/${this.context.orgNo}/drives`,
          onSuccess: (res) => {
            resolve(res.data);
            return [];
          },
          onFailure: () => {
            reject(new Error("Failed to load drives"));
            return [];
          },
        }),
      ]);
    });
  }

  loadContentInSharepointDrive(
    driveID: string,
    parentID: string | undefined,
  ): Promise<SharepointContentInDrive[]> {
    return new Promise((resolve, reject) => {
      this.dispatch([
        httpGet({
          url: `/sharepoint/${this.context.orgNo}/drives/${driveID}${parentID ? `/items/${parentID}` : ""}`,
          onSuccess: (res) => {
            resolve(res.data);
            return [];
          },
          onFailure: () => {
            reject(new Error("Failed to load content in drive"));
            return [];
          },
        }),
      ]);
    });
  }

  saveSharepointDestinationSettings(
    documentType: DocumentType,
  ): (destination?: SharepointContentInDrive) => Promise<void> {
    return (destination?: SharepointContentInDrive) =>
      new Promise((resolve, reject) => {
        this.dispatch([
          httpPost({
            url: `/sharepoint/${this.context.orgNo}/document-settings`,
            data: {
              destinationFolderId: destination?.id,
              destinationFolderName: destination?.name,
              driveId: destination?.parentReference.driveId ?? "",
              documentType,
            },
            onSuccess: (res) => {
              resolve(res.data);
              return [];
            },
            onFailure: () => {
              reject(new Error("Failed to save sharepoint settings for document"));
              return [];
            },
          }),
        ]);
      });
  }

  openSendForReviewDialog(
    { messageTemplate, document, recipients }: OpenSendForReviewDialogAction,
    canBeSentForVoting: boolean,
  ) {
    this.dialogState = {
      type: "sendForReview",
      model: new SendForReviewDialogModel({
        baseUrl: window.location.href,
        orgNo: this.context.orgNo,
        projectId: this.context.projectId,
        template: messageTemplate,
        document: document,
        dispatch: this.dispatch,
        closeDialog: this.closeDialog,
        roles: sortedBoardAndCEO(this.context.initialState.roles),
        canBeSentForVoting: canBeSentForVoting,
        recipients: recipients,
        context: this.context,
      }),
    };
  }

  openSigningStatusDialog({ document }: { document: DocumentItem }) {
    this.dialogState = {
      type: "viewDocumentSigningStatus",
      model: new ViewDocumentSigningStatusDialogModel({
        documentId: document.documentId,
        documentType: document.type,
        title: document.title,
        resendDocumentForSigning: (signedDocumentId, data) =>
          this.dispatch({
            type: "dbot/resend-document-for-signing",
            documentId: document.documentId,
            signedDocumentId,
            ...data,
          }),
        getSigningStatus: () =>
          this.dispatch([getSigningStatusEvent(document.documentId), refreshProjectStatusEvent()]),
        downloadSignedDocument: () =>
          this.dispatch(downloadSignedDocumentEvent(document.documentId, document.type)),
        closeDialog: this.closeDialog,
        confirmDeleteSignatureRequest: () =>
          this.dispatch(reopenDocumentEvent(document.documentId, true)),
      }),
    };
    this.dialogState.model.init();
  }

  openSubscriptionStatusDialog({ document }: { document: DocumentItem }) {
    this.dialogState = {
      type: "viewSubscriptionStatus",
      model: new ViewSubscriptionStatusDialogModel({
        documentId: document.documentId,
        title: document.title,
        getSubscriptionStatus: () => this.dispatch(getSubscriptionStatusEvent(document.documentId)),
        closeDialog: this.closeDialog,
        shareTypes: this.context.initialState.shareTypes,
      }),
    };
    this.dialogState.model.init();
  }

  openSubscriptionFormBatchPreviewDialog({
    title,
    filePreview,
  }: OpenSubscriptionFormBatchPreviewDialogAction) {
    when(findIncreaseOfShareCaptial(this.context), (data) => {
      this.dialogState = {
        type: "viewSubscriptionFormBatchPreview",
        model: new ViewSubscriptionFormBatchPreviewModel({
          title,
          filePreview,
          context: this.context,
          closeDialog: this.closeDialog,
          subscribersData: toIncreaseOfShareCapitalSubscribersStatus(data),
        }),
      };
    });
  }

  openConfirmSendToBRDialog(orgNo: string, decisionId: string) {
    const onSubmit = (signers: Record<string, BRSigner[]>, decisions: SendToBRDecision[]) =>
      this.dispatch(sendToBREvent(orgNo, decisionId, decisions, signers));

    this.dialogState = {
      type: "confirmSendToBR",
      model: new ConfirmSendToBRDialogModel(
        orgNo,
        decisionId,
        this.sendToBRCompanies,
        onSubmit,
        this.closeDialog,
      ),
    };
  }

  get sendToBRCompanies(): CompanyBRContext[] {
    const decisionsByOrgNo = groupBy(this.decisionsForApply, (item) => item.orgNo);
    const companyNameByOrgNo = new Map(this.context.companies.map((c) => [c.orgNo, c.name]));
    return compact(
      Object.entries(decisionsByOrgNo).map(([orgNo, decisions]) => {
        const matters = compact(decisions.map((decision) => this.toSendToBRMatter(decision)));
        if (matters.length === 0) {
          return undefined;
        }
        return {
          orgNo,
          name: companyNameByOrgNo.get(orgNo) ?? "",
          matters,
          signers: decisions[0].sendToBRSigners,
        };
      }),
    );
  }

  toSendToBRMatter(item: DecisionListItem): SendToBRMatter | undefined {
    const isNotSubmitted = (f: FollowUp) => f.status !== FollowUpStatus.COMPLETED;
    const sendRegistrationToBR = item.followUps.find(
      (f) => f.data.type === FollowUpType.sendToBR && f.data.isRegistration && isNotSubmitted(f),
    );
    const sendToBR = item.followUps.find(
      (f) => f.data.type === FollowUpType.sendToBR && !f.data.isRegistration && isNotSubmitted(f),
    );
    const followUp = sendRegistrationToBR ?? sendToBR;
    if (followUp?.data.type !== FollowUpType.sendToBR) {
      return undefined;
    }
    return {
      decisionId: item.decision.decisionId,
      isRegistration: followUp.data.isRegistration,
      title: getDecisionName(item.decision.type),
      isReady: isSendToBRReady(item, followUp),
      brStatus: followUp.data.brStatus,
      additionalSigners: followUp.data.additionalSigners,
    };
  }

  openConfirmSendIncorporationToBRDialog(orgNo: string, decisionId: string) {
    const onSubmit = () => this.dispatch(sendIncorporationToBREvent(orgNo, decisionId));
    this.dialogState = {
      type: "confirmSendIncorporationToBR",
      model: new ConfirmSendIncorporationToBRDialogModel(
        onSubmit,
        this.closeDialog,
        this.context.featureToggles.incorporation ?? false,
      ),
    };
    // Kick off BR's prekontroll immediately on open — the dialog's submit button spins until this
    // returns, then becomes clickable on success, or is replaced by the BR error on failure.
    this.dispatch(prekontrollIncorporationEvent(orgNo, decisionId));
  }

  skipSendToBR(orgNo: string, decisionId: string) {
    if (this.isSkippingSendToBR.some((x) => x.orgNo === orgNo && x.decisionId === decisionId)) {
      return;
    }
    this.isSkippingSendToBR.push({ orgNo, decisionId });
    this.dispatch(skipSendToBREvent(orgNo, decisionId));
  }

  openSkipConfirmSendToBRDialog(orgNo: string, decisionId: string) {
    this.dispatch(
      confirmAction({
        text: { key: "dbot.summary.sendToBR.skip.confirm" },
        confirmLabel: { key: "confirm" },
        onConfirm: [thunk(() => this.skipSendToBR(orgNo, decisionId))],
      }),
    );
  }
}

export type DocumentListItem = {
  document: DocumentItem;
  signingStatus: DocumentSigningStatus;
  numberOfSigners: number;
  numberOfSignatures: number;
  incorporationDocumentPart?: IncorporationDocumentPart;
  isRefreshingSigningStatus: boolean;
  sendingTime: string | undefined;
  showDocumentListItem: boolean;
};

export type IdentificationForPerson = {
  id?: string;
  companyRoleId?: number;
  name: string;
  nationality?: string;
  identityNumber: string;
  dnr?: string;
  email?: string;
};

export class IdentificationForPersonForm {
  data: IdentificationForPerson;
  identityNumberModel: StringFieldModel;
  emailModel: StringFieldModel;
  name: string;

  nationality: RadioFieldModel<{ code: string; name: string }>;
  dnr: StringFieldModel;

  constructor(person: IdentificationForPerson, locale: Language) {
    this.data = person;
    this.identityNumberModel = new StringFieldModel({
      value: person.identityNumber,
      required: person.id === undefined || person.nationality === norway,
      validator: (value) =>
        person.identityNumber !== "" &&
        value.replace(/\s/g, "") === person.identityNumber.replace(/\s/g, "")
          ? undefined
          : norwegianPersonalIdValidator(value),
    });
    this.emailModel = new StringFieldModel({
      value: person.email ?? "",
      required: true,
      validator: emailValidator,
    });
    this.name = person.name;

    this.nationality = makeCountryRadioFieldModel(locale, person.nationality);
    this.dnr = new StringFieldModel({
      value: person.dnr,
      required: person.id !== undefined && person.nationality !== norway,
      validator: dNumberValidator,
    });
  }

  setNationality(value: { code: string; name: string }) {
    this.nationality.setValue(value);
    this.dnr.setRequired(value.code !== norway);
    this.identityNumberModel.setRequired(value.code === norway);
  }

  get showNationality() {
    return this.data.id !== undefined;
  }

  get showDnr() {
    return this.showNationality && this.nationality.value.code !== norway;
  }

  toJSON() {
    return this.data.id
      ? {
          id: this.data.id,
          companyRoleId: this.data.companyRoleId,
          name: this.name,
          nationality: this.nationality.value.code,
          socialSecurityNumber: this.identityNumberModel.validatedValue,
          dnr: this.dnr.validatedValue,
          email: this.emailModel.validatedValue,
        }
      : {
          name: this.name,
          identityNumber: this.identityNumberModel.validatedValue,
          email: this.emailModel.validatedValue,
        };
  }

  get isValid() {
    return this.identityNumberModel.isValid && this.emailModel.isValid && this.dnr.isValid;
  }

  validate(): boolean {
    this.identityNumberModel.validate();
    this.emailModel.validate();
    this.dnr.validate();
    return this.isValid;
  }
}

export class ProvideIdentificationToPersonsForm {
  identifications: IdentificationForPersonForm[] = [];
  private readonly dispatch: (action: DecisionAction) => void;
  readonly attachmentType: AttachmentType;
  readonly orgNo: string;
  readonly decisionId: string;
  readonly followUp: FollowUp;

  constructor(
    dispatch: (action: DecisionAction) => void,
    attachmentType: AttachmentType,
    orgNo: string,
    decisionId: string,
    followUp: FollowUp,
    persons: IdentificationForPerson[],
    locale: Language,
  ) {
    this.dispatch = dispatch;
    this.attachmentType = attachmentType;
    this.decisionId = decisionId;
    this.orgNo = orgNo;
    this.followUp = followUp;
    this.identifications = persons.map((p) => new IdentificationForPersonForm(p, locale));
    makeAutoObservable(this, {}, { autoBind: true });
  }

  validateAndSave() {
    if (this.identifications.every((i) => i.validate())) {
      const input = JSON.stringify(this.identifications.map((i) => i.toJSON()));
      const payload = new TextEncoder().encode(input);
      this.dispatch(
        uploadAttachmentsEvent({
          source: this.followUp,
          files: [
            new File([payload], "identifications.json", {
              type: "text/plain",
            }),
          ],
          attachmentType: this.attachmentType,
          reference: this.decisionId,
          overrideExistingAttachmentType: true,
          onSuccess: [updateFollowUpStatusEvent(this.orgNo, this.decisionId, this.followUp.data)],
        }),
      );
    }
  }
}

export type AttachmentConfirmerOption = "auditor" | "accountant" | "other";

function confirmerOptionRole(option: AttachmentConfirmerOption): TMessage {
  switch (option) {
    case "auditor":
      return { key: "dbot.summary.attachment.confirmer.auditor" };
    case "accountant":
      return { key: "dbot.summary.attachment.confirmer.accountant" };
    case "other":
      return { key: "dbot.summary.attachment.confirmer.other" };
  }
}

function confirmerOptionLabel(
  option: AttachmentConfirmerOption,
  company: AuditorListItem | AccountantListItem | undefined,
): TMessage {
  const role = confirmerOptionRole(option);
  return company
    ? {
        key: "dbot.summary.attachment.confirmer.company",
        params: {
          role,
          company: { translated: `${company.name} (${formatOrgNum(company.organisationNumber)})` },
        },
      }
    : role;
}

export class AttachmentFollowUpForm {
  email: StringFieldModel;
  attachDocumentTypes: BooleanFieldModel;
  confirmerOptionModel: RadioFieldModel<AttachmentConfirmerOption | undefined>;
  confirmerSearchModel: StringFieldModel;
  suggestedConfirmerCompanies: SuggestedCompany[] = [];
  selectedConfirmerCompany?: SuggestedCompany;

  data: AttachmentFollowUp;
  readonly dispatch: DbotEventDispatcher;
  readonly alertStore: AlertMessageStore;
  readonly attachmentType: AttachmentType;
  readonly orgNo: string;
  readonly decisionId: string;
  readonly currentAuditor?: AuditorListItem;
  readonly currentAccountant?: AccountantListItem;
  private readonly searchConfirmerCompanies: (input: string) => void;

  addAttachableDocumentsResult?: Result<TMessage, TMessage>;

  constructor(
    alertStore: AlertMessageStore,
    dispatch: DbotEventDispatcher,
    attachmentType: AttachmentType,
    orgNo: string,
    decisionId: string,
    data: AttachmentFollowUp,
    initialState?: Evaluation,
  ) {
    this.dispatch = dispatch;
    this.alertStore = alertStore;
    this.attachmentType = attachmentType;
    this.orgNo = orgNo;
    this.decisionId = decisionId;
    this.data = data;
    this.currentAuditor = initialState?.currentAuditor ?? undefined;
    this.currentAccountant = initialState?.currentAccountant ?? undefined;
    this.attachDocumentTypes = new BooleanFieldModel({
      required: data.attachableDocumentTypes.length > 0,
      value: data.attachDocumentTypes,
      onChange: (value) => this.saveAttachDocumentType(value),
    });
    this.email = createEmailField({
      value: data.email,
      required: data.requiresEmail,
      onChange: (email) => this.saveEmail(email),
    });

    const initialOption = this.initialConfirmerOption(data.confirmer);
    if (initialOption === "other" && data.confirmer?.name && data.confirmer.organisationNumber) {
      this.selectedConfirmerCompany = {
        name: data.confirmer.name,
        organizationnumber: data.confirmer.organisationNumber,
      };
    }
    this.confirmerOptionModel = new RadioFieldModel<AttachmentConfirmerOption | undefined>({
      value: initialOption,
      options: this.availableConfirmerOptions,
      idFn: (o) => o ?? "",
      labelFn: (o) =>
        o ? confirmerOptionLabel(o, this.confirmerOptionCompany(o)) : { translated: "" },
      onChange: (o) => this.onConfirmerOptionChange(o),
    });
    this.confirmerSearchModel = new StringFieldModel({
      value: this.selectedConfirmerCompany?.name ?? "",
    });
    this.searchConfirmerCompanies = debounce((input: string) => this.searchCompanies(input), 250);

    makeAutoObservable(this, {}, { autoBind: true });
  }

  get availableConfirmerOptions(): AttachmentConfirmerOption[] {
    return compact([
      this.currentAuditor ? "auditor" : undefined,
      this.currentAccountant ? "accountant" : undefined,
      "other",
    ]);
  }

  get isOtherConfirmerSelected(): boolean {
    return this.confirmerOptionModel.value === "other";
  }

  private initialConfirmerOption(
    confirmer?: CompanyContact,
  ): AttachmentConfirmerOption | undefined {
    if (!confirmer) {
      return undefined;
    }
    return (
      this.availableConfirmerOptions.find(
        (option) =>
          confirmer.organisationNumber !== undefined &&
          this.confirmerOptionCompany(option)?.organisationNumber === confirmer.organisationNumber,
      ) ?? "other"
    );
  }

  handleEvent(event: DbotEvent) {
    if (event.type === "dbot/search-companies-result" && event.source === this) {
      this.suggestedConfirmerCompanies = event.result;
    }
  }

  confirmerOptionCompany(option: AttachmentConfirmerOption) {
    switch (option) {
      case "auditor":
        return this.currentAuditor;
      case "accountant":
        return this.currentAccountant;
      case "other":
        return undefined;
    }
  }

  onConfirmerOptionChange(option: AttachmentConfirmerOption | undefined) {
    const company = option ? this.confirmerOptionCompany(option) : undefined;
    this.selectedConfirmerCompany = undefined;
    this.confirmerSearchModel.clear();
    if (company) {
      this.saveAttachmentConfirmer({
        name: company.name,
        organisationNumber: company.organisationNumber,
      });
    } else {
      this.saveAttachmentConfirmer({});
    }
  }

  clearSelectedConfirmerCompany() {
    this.confirmerSearchModel.clear();
    this.selectedConfirmerCompany = undefined;
    this.saveAttachmentConfirmer({});
  }

  setConfirmerCompanySearch(input: string) {
    this.confirmerSearchModel.setInputValue(input);
    this.searchConfirmerCompanies(input);
  }

  revertConfirmerCompanySearch() {
    this.confirmerSearchModel.setAndCommitInputValue(this.selectedConfirmerCompany?.name ?? "");
  }

  setSelectedConfirmerCompany(company: SuggestedCompany) {
    this.confirmerSearchModel.setAndCommitInputValue(company.name);
    this.selectedConfirmerCompany = company;
    this.saveAttachmentConfirmer({
      name: company.name,
      organisationNumber: company.organizationnumber,
    });
  }

  searchCompanies(input: string) {
    this.dispatch([{ type: "dbot/search-companies", input, source: this }]);
  }

  saveAttachmentConfirmer(confirmer: CompanyContact | undefined) {
    const data: AttachmentFollowUp = { ...this.data, confirmer };
    this.dispatch(updateFollowUpStatusEvent(this.orgNo, this.decisionId, data));
  }

  get requiresConfirmer() {
    return this.data.requiresConfirmer;
  }

  get requiresEmail() {
    return this.data.requiresEmail;
  }

  get hasAttachableDocumentTypes() {
    return this.data.attachableDocumentTypes.length > 0;
  }

  showAddAttachableDocuments(attachments: DecisionAttachment[], documents: DocumentItem[]) {
    return (
      (this.hasAttachableDocumentTypes &&
        this.attachDocumentTypes.value !== true &&
        attachments.length === 0) ||
      this.getMissingAttachableDocumentTypes(documents).length > 0
    );
  }

  showAttachableDocuments(documents: DocumentItem[]) {
    return (
      this.hasAttachableDocumentTypes &&
      this.attachDocumentTypes.value === true &&
      this.getMissingAttachableDocumentTypes(documents).length === 0
    );
  }

  isAttachableDocumentsCompleted(
    documentItems: DocumentItem[],
    documentStatuses: ProjectDocumentStatus[],
  ): boolean {
    const isSigningComplete = (documentId: string) =>
      documentStatuses.some(
        (ds) =>
          ds.documentId === documentId &&
          ds.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE,
      );

    return this.data.attachableDocumentTypes.every((attachableDocumentType) => {
      const documents = this.getDocumentItems(documentItems, attachableDocumentType);
      const hasDocuments = documents.length > 0;

      return (
        hasDocuments &&
        (!attachableDocumentType.requiresSigning ||
          documents.every((d) => isSigningComplete(d.documentId)))
      );
    });
  }

  getAllAttachableDocuments(documents: DocumentItem[]) {
    return this.data.attachableDocumentTypes.flatMap((attachableDocumentType) =>
      this.getDocumentItems(documents, attachableDocumentType),
    );
  }

  getMissingAttachableDocumentTypes(documents: DocumentItem[]) {
    return this.data.attachableDocumentTypes.filter(
      (a) => this.getDocumentItems(documents, a).length === 0,
    );
  }

  addBoardStatementDocument(documents: DocumentItem[], documentStatuses: ProjectDocumentStatus[]) {
    const missingAttachableDocumentTypes = this.getMissingAttachableDocumentTypes(documents);
    if (missingAttachableDocumentTypes.length > 0) {
      this.addAttachableDocumentsResult = pending();
      this.dispatch(
        missingAttachableDocumentTypes?.map((d) => ({
          type: "dbot/add-documents",
          documentTypes: [d.documentType],
          customDocumentType: d.customDocumentType,
          onDone: (err) => [
            thunk(() => {
              this.addAttachableDocumentsResult = undefined;
              if (!err) {
                this.setAttachDocumentType(documents, documentStatuses);
              }
            }),
          ],
        })) ?? [],
      );
    } else {
      this.setAttachDocumentType(documents, documentStatuses);
    }
  }

  setAttachDocumentType(documents: DocumentItem[], documentStatuses: ProjectDocumentStatus[]) {
    this.attachDocumentTypes.setValue(true);
    if (this.isAttachableDocumentsCompleted(documents, documentStatuses)) {
      this.alertStore.showSuccess({ key: "dbot.summary.attachment.attachable-document-completed" });
    } else {
      this.alertStore.showInfo({ key: "dbot.summary.attachment.attachable-document-added" });
    }
  }

  getDocumentItems(documents: DocumentItem[], attachable: AttachableDocumentType) {
    return documents.filter(
      (d) =>
        (d.documentData?.type === DocumentType.custom_document &&
          attachable.documentType === d.documentData?.type &&
          attachable.customDocumentType === d.documentData.customDocumentType) ||
        (d.documentData?.type !== DocumentType.custom_document &&
          attachable.documentType === d.documentData?.type),
    );
  }

  saveEmail(email: string | undefined) {
    const data: AttachmentFollowUp = {
      ...this.data,
      email: email,
    };
    this.dispatch(updateFollowUpStatusEvent(this.orgNo, this.decisionId, data));
  }

  saveAttachDocumentType(value: boolean) {
    const data: AttachmentFollowUp = {
      ...this.data,
      attachDocumentTypes: value,
    };
    this.dispatch(updateFollowUpStatusEvent(this.orgNo, this.decisionId, data));
  }
}

function hasAllBoardMemberSSNs(context: ProjectContext) {
  const companyRoles = context.initialState.roles;
  const participants = context.boardMeetingNoticeRecipients;
  const participatingRoles = companyRoles.filter((r) =>
    participants.some((p) => p.id === r.companyroleId),
  );
  return participatingRoles.every((r) => r.socialSecurityNumber);
}

function hasExtraParticipants(context: ProjectContext) {
  const companyRoles = context.initialState.roles;
  const participants = context.boardMeetingNoticeRecipients;
  return participants.some((p) => !companyRoles.some((r) => p.id === r.companyroleId));
}

function isMissingShareholderSSN(context: ProjectContext) {
  return shareholdersWithShares(
    context.initialState.shareholders,
    context.initialState.sharesForShareholders,
  )
    .filter((s) => isCountryCodeNorway(s.nationality) || isCountryCodeSweden(s.nationality))
    .some((s) =>
      s.isCompany ? !s.contactInfo?.socialSecurityNumber : !s.personInfo?.socialSecurityNumber,
    );
}

function hasForeignShareholders(context: ProjectContext) {
  return shareholdersWithShares(
    context.initialState.shareholders,
    context.initialState.sharesForShareholders,
  ).some((s) => !isCountryCodeNorway(s.nationality) && !isCountryCodeSweden(s.nationality));
}

function findSharepointSettings(
  context: ProjectContext,
  documentId: string,
  documentType: DocumentType,
): SharepointSettingsForDocument | undefined {
  const result = context.documentSigners.get(documentId);
  if (isOk(result)) {
    return result.data.companySettings
      ?.find((i) => i.orgNo === context.orgNo)
      ?.sharepointSettings?.settings.find((s) => s.documentType === documentType);
  } else {
    return undefined;
  }
}

function currentDocumentId(documents: BundledDocument[]): string {
  return (documents.find((d) => d.isCurrent) ?? documents[0]).documentId;
}
